import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import {
  DeleteObjectDialog,
  EditObjectEditor,
} from 'widgets/kyrgyzstan/edit-object-editor'
import type { EditObjectListItem } from 'widgets/kyrgyzstan/edit-object-editor'

import { getRequiredBookingConfirmationValue } from 'entities/kyrgyzstan/lib'
import {
  editObjectSessionStore,
  loadEditObjectRevisionByObjectId,
} from 'entities/kyrgyzstan/model'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { KyrgyzstanEditObjectApi } from 'shared/api/kyrgyzstan/edit-object'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

const KyrgyzstanEditObjectPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { objectId } = useParams<{ objectId: string }>()
  const { closeDialog, openDialog } = useDialogBottomSheet()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const currentDraft = editObjectSessionStore.getDraftByObjectId(objectId)
  const draftId = currentDraft?.id ?? null
  const isDirty = editObjectSessionStore.isDirty
  const bookingConfirmed = getRequiredBookingConfirmationValue(
    currentDraft?.booking?.booking_confirmed,
  )

  useEffect(() => {
    if (!objectId) {
      return
    }

    const abortController = new AbortController()

    const loadDraft = async () => {
      setIsInitialLoading(true)
      setError(null)

      try {
        const draft = await loadEditObjectRevisionByObjectId(
          objectId,
          editObjectSessionStore.objectId,
          editObjectSessionStore.draftId,
          abortController.signal,
        )

        if (!abortController.signal.aborted) {
          editObjectSessionStore.setObjectDraft(draft.realty_object_id, draft)
        }
      } catch (requestError) {
        const errorMessage = getApiErrorMessage(requestError)

        if (abortController.signal.aborted || errorMessage === null) {
          return
        }

        setError(errorMessage)
        showApiErrorDialog(requestError)
      } finally {
        if (!abortController.signal.aborted) {
          setIsInitialLoading(false)
        }
      }
    }

    loadDraft()

    return () => {
      abortController.abort()
    }
  }, [objectId, showApiErrorDialog])

  const handleItemClick = (item: EditObjectListItem) => {
    if (!objectId || !draftId || !item.stepId) {
      return
    }

    navigate(
      RoutingPaths.kyrgyzstan.objects.edit.step.path(objectId, item.stepId),
    )
  }

  const handleDeleteClick = () => {
    openDialog({
      child: (
        <DeleteObjectDialog
          onConfirm={handleDeleteConfirm}
          onError={showApiErrorDialog}
        />
      ),
      isDialog: false,
      borderRadius: 20,
      padding: [20, 8, 8],
    })
  }

  const handleDeleteConfirm = async () => {
    if (!objectId) {
      closeDialog()
      return
    }

    await KyrgyzstanEditObjectApi.updateObjectActive(objectId, {
      is_active: false,
    })
    closeDialog()
    editObjectSessionStore.reset()
    navigate(RoutingPaths.kyrgyzstan.objects.path(), { replace: true })
  }

  const handleSaveClick = async () => {
    if (!draftId || isSubmitting || !isDirty) {
      return
    }

    setIsSubmitting(true)

    try {
      await KyrgyzstanCreateObjectApi.saveBookingStep(draftId, {
        booking_confirmed: getRequiredBookingConfirmationValue(
          currentDraft?.booking?.booking_confirmed,
        ),
      })
      await KyrgyzstanEditObjectApi.submitDraftRevision(draftId)
      editObjectSessionStore.reset()
      navigate(
        {
          pathname: RoutingPaths.kyrgyzstan.objects.path(),
          search: '?status=submitted',
        },
        { replace: true },
      )
    } catch (requestError) {
      showApiErrorDialog(requestError)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <EditObjectEditor
      actionLabel={t('kyrgyzstan_block.create_object.actions.save')}
      bookingConfirmed={bookingConfirmed}
      draft={currentDraft}
      error={error}
      isDirty={isDirty}
      isInitialLoading={isInitialLoading}
      isSubmitting={isSubmitting}
      onBack={() => navigate(RoutingPaths.kyrgyzstan.objects.path())}
      onDelete={handleDeleteClick}
      onItemClick={handleItemClick}
      onSubmit={handleSaveClick}
    />
  )
})

export default KyrgyzstanEditObjectPage
